/** PWA 内置业务提示词；用户修改值会单独保存在 IndexedDB。 */
export const DEFAULT_PROMPTS = Object.freeze({
  daily_standard: "You are a friendly conversation partner. Reply directly to the user, keep the conversation moving, and never include correction or coaching in the reply itself.",
  daily_american: "You are a friendly American conversation partner. Reply directly with natural American wording, contractions and concise everyday speech; never include correction or coaching in the reply itself.",
  daily_scoring: "Evaluate the user's spoken English fairly. Give specific evidence and one immediately usable improved expression.",
  transcript_correction: "Correct only likely speech-recognition mistakes, Chinese misrecognition and punctuation. Preserve genuine grammar mistakes and intended meaning.",
  part1: "Act as an IELTS Speaking examiner for Part 1. Ask concise questions about familiar topics without coaching inside the question.",
  part2_random_card: "Create an original IELTS-style Part 2 cue card. Do not claim it is an official or recalled examination question.",
  part3: "Act as an IELTS Speaking examiner for Part 3. Ask increasingly abstract discussion questions related to the Part 2 topic.",
  help: "Give brief vocabulary and sentence starters that help the learner form an answer without writing a full memorized response.",
  ielts_single_scoring: "Estimate this practice performance against the four IELTS Speaking criteria and provide evidence-based advice.",
  ielts_mock_scoring: "Assess the complete three-part practice as one performance against the four equal IELTS Speaking criteria.",
});

/** PWA 提示词编辑器显示名称。 */
export const PROMPT_LABELS = Object.freeze({
  daily_standard: "日常对话 - 标准模式",
  daily_american: "日常对话 - 美式地道模式",
  daily_scoring: "日常对话评分",
  transcript_correction: "转写纠正",
  part1: "PART1 提问",
  part2_random_card: "PART2 随机题卡",
  part3: "PART3 提问",
  help: "请求帮助",
  ielts_single_scoring: "单项 IELTS 评分",
  ielts_mock_scoring: "完整模考评分",
});

/** 模型生成失败时使用的本地 PART1 问题。 */
export const FALLBACK_PART1 = Object.freeze({
  topics: ["Daily routines", "Technology"],
  questions: [
    "What part of your daily routine do you enjoy most?",
    "Has your daily routine changed in recent years?",
    "Is there anything you would like to change about your routine?",
    "Do you think routines are important for young people?",
    "What kind of technology do you use every day?",
    "How has technology changed the way you communicate?",
    "Is there any technology you find difficult to use?",
    "What new technology would you like to try in the future?",
  ],
});

/** 60 道内置题卡的标题、描述对象、分类和讨论主题。 */
const CARD_SPECS = [
  ["Describe a teacher who helped you", "this teacher", "people", "the influence of teachers"],
  ["Describe a friend you enjoy talking with", "this friend", "people", "friendship and communication"],
  ["Describe a family member you admire", "this family member", "people", "family influence"],
  ["Describe a person who is good at a practical skill", "this person", "people", "practical skills"],
  ["Describe someone who encouraged you", "this person", "people", "motivation and encouragement"],
  ["Describe an interesting person you met recently", "this person", "people", "first impressions"],
  ["Describe a person who works to protect the environment", "this person", "people", "environmental responsibility"],
  ["Describe an older person whose stories you remember", "this older person", "people", "learning across generations"],
  ["Describe a quiet place you like", "this place", "places", "quiet public spaces"],
  ["Describe a crowded place you visited", "this place", "places", "crowding in cities"],
  ["Describe a place where you learned something", "this place", "places", "learning environments"],
  ["Describe a shop you often visit", "this shop", "places", "local shops and online retail"],
  ["Describe a natural place you want to protect", "this place", "places", "nature conservation"],
  ["Describe a city you would like to live in", "this city", "places", "urban quality of life"],
  ["Describe a public building you find useful", "this building", "places", "public architecture"],
  ["Describe a place where you exercise", "this place", "places", "access to exercise facilities"],
  ["Describe a useful object in your home", "this object", "objects", "consumer products"],
  ["Describe a gift that was meaningful to you", "this gift", "objects", "gift-giving customs"],
  ["Describe a piece of clothing you enjoy wearing", "this clothing", "objects", "fashion and identity"],
  ["Describe an old object your family keeps", "this object", "objects", "preserving family history"],
  ["Describe a photo that matters to you", "this photo", "objects", "photography and memory"],
  ["Describe a tool or device that saves you time", "this device", "objects", "technology and productivity"],
  ["Describe a book you would recommend", "this book", "media", "reading habits"],
  ["Describe a film that made you think", "this film", "media", "the social influence of films"],
  ["Describe a song you remember well", "this song", "media", "music and memory"],
  ["Describe a news story you followed", "this news story", "media", "trust in news media"],
  ["Describe an advertisement you noticed", "this advertisement", "media", "advertising and consumer choice"],
  ["Describe a website you find useful", "this website", "media", "access to online information"],
  ["Describe a celebration you enjoyed", "this celebration", "experiences", "the role of celebrations"],
  ["Describe a journey that did not go as planned", "this journey", "experiences", "planning and uncertainty"],
  ["Describe a time you learned from a mistake", "this experience", "experiences", "attitudes toward mistakes"],
  ["Describe an activity you tried for the first time", "this activity", "experiences", "trying unfamiliar activities"],
  ["Describe a time you helped another person", "this experience", "experiences", "volunteering and mutual help"],
  ["Describe a meal you shared with other people", "this meal", "experiences", "social meaning of food"],
  ["Describe a time you had to wait for something", "this experience", "experiences", "patience in modern life"],
  ["Describe a positive change in your routine", "this change", "experiences", "changing personal habits"],
  ["Describe a skill you want to improve", "this skill", "skills", "lifelong learning"],
  ["Describe something you learned outside school", "this learning experience", "skills", "informal education"],
  ["Describe a difficult task you completed", "this task", "skills", "persistence and achievement"],
  ["Describe a language-learning method that works for you", "this method", "skills", "language education"],
  ["Describe a creative activity you enjoy", "this activity", "skills", "creativity in education and work"],
  ["Describe a sport you would like to learn", "this sport", "skills", "participation in sport"],
  ["Describe a job you would like to try", "this job", "work", "career choice"],
  ["Describe a workplace you know about", "this workplace", "work", "workplace design"],
  ["Describe a useful rule at school or work", "this rule", "work", "rules and individual freedom"],
  ["Describe a team project you participated in", "this project", "work", "teamwork"],
  ["Describe a business you would like to start", "this business", "work", "entrepreneurship"],
  ["Describe a goal you hope to achieve", "this goal", "future", "setting personal goals"],
  ["Describe a country you want to visit", "this country", "future", "international tourism"],
  ["Describe a change you expect in your hometown", "this change", "future", "urban development"],
  ["Describe a course you want to take", "this course", "future", "access to further education"],
  ["Describe an environmental improvement you want to see", "this improvement", "future", "environmental policy"],
  ["Describe a healthy habit you want to develop", "this habit", "future", "public health and personal choice"],
  ["Describe a tradition that is important locally", "this tradition", "society", "protecting traditions"],
  ["Describe a local problem that should be solved", "this problem", "society", "local government priorities"],
  ["Describe a public service you have used", "this service", "society", "quality of public services"],
  ["Describe a rule that makes a community better", "this rule", "society", "community responsibility"],
  ["Describe a form of transport you often use", "this transport", "society", "sustainable transport"],
  ["Describe a market or public event in your area", "this event", "society", "community life"],
  ["Describe a food that represents your region", "this food", "society", "food and cultural identity"],
];

/** 各题材的四条题卡提示模板。 */
const CUE_POINT_TEMPLATES = {
  people: ["who {subject} is", "how you know {subject}", "what {subject} is like or does", "and explain why {subject} is important to you"],
  places: ["where {subject} is", "when you visited or learned about {subject}", "what {subject} is like", "and explain why {subject} interests you"],
  objects: ["what {subject} is", "when and how you got {subject}", "how you use or value {subject}", "and explain why {subject} matters to you"],
  media: ["what {subject} is about", "when you first saw, heard or used {subject}", "what you remember most about {subject}", "and explain why {subject} affected you"],
  experiences: ["when and where {subject} happened", "who was involved", "what happened", "and explain why {subject} was memorable"],
  skills: ["what {subject} is", "how you learned or plan to learn {subject}", "what challenges it involves", "and explain why you want to develop {subject}"],
  work: ["what {subject} is", "where and when it takes place", "what responsibilities or activities it involves", "and explain why {subject} interests you"],
  future: ["what {subject} is", "why you want or expect it", "what would be needed to make it happen", "and explain why {subject} is important to you"],
  society: ["what {subject} is", "where you encounter it in your area", "how it affects local people", "and explain why {subject} is significant"],
};

/** 各题材的固定收尾问题。 */
const ROUNDING_QUESTIONS = {
  people: "Would you like to spend more time with this person in the future?",
  places: "Would you like to visit this place again?",
  objects: "Do you think you will keep using or valuing it in the future?",
  media: "Would you recommend it to other people?",
  experiences: "Would you like to have a similar experience again?",
  skills: "Would you like to develop this skill further?",
  work: "Would you like to do something similar again?",
  future: "How likely are you to do this in the near future?",
  society: "Do you think it will remain important in the future?",
};

/**
 * 根据固定规格生成可恢复的 60 道内置题卡。
 *
 * @returns {Array<object>} ID 稳定且默认启用的题卡数组。
 */
export function buildBuiltinCards() {
  return CARD_SPECS.map(([title, subject, category, discussion], index) => ({
    id: `builtin-${String(index + 1).padStart(3, "0")}`,
    title,
    cue_points: CUE_POINT_TEMPLATES[category].map((point) => point.replaceAll("{subject}", subject)),
    rounding_question: ROUNDING_QUESTIONS[category],
    part3_topic: discussion,
    part3_questions: [
      `Why is ${discussion} important in modern society?`,
      `How has ${discussion} changed in recent years?`,
      `Do different generations think differently about ${discussion}?`,
      `What could governments or communities do about ${discussion}?`,
    ],
    category,
    tags: [category, discussion],
    source: "builtin",
    enabled: true,
  }));
}
